
import React from 'react';
import { UserProfile, Client } from '@/shared/types';

interface TemplateProps {
  profile: UserProfile;
  client?: Client | null;
  title: string;
  metaData: { label: string; value: string }[];
  children: React.ReactNode;
  footerNote?: string;
  type: string;
  containerClass: string;
  LogoImage: React.FC<{ size: 'sm' | 'md' | 'lg' | 'xl'; className?: string; light?: boolean }>;
  hideHeader?: boolean;
}

const TechTemplate: React.FC<TemplateProps> = ({
  profile, client, title, metaData, children, footerNote, type, containerClass, LogoImage, hideHeader = false
}) => {
  return (
    <div className={`${containerClass} bg-[#0f172a] text-[#e2e8f0] font-mono`}>
      {!hideHeader && (
        <>
          {/* Terminal Header */}
          <div className="bg-[#1e293b] border-b border-[#334155] p-2 flex items-center justify-between">
              <div className="flex gap-2 ml-2">
                  <div className="w-3 h-3 rounded-full bg-error-500"></div>
                  <div className="w-3 h-3 rounded-full bg-warning-500"></div>
                  <div className="w-3 h-3 rounded-full bg-success-500"></div>
              </div>
              <div className="text-xs text-neutral-500 font-bold">bash -- {type.toLowerCase()}.sh</div>
              <div className="w-10"></div>
          </div>

          <div className="p-10 flex-1 flex flex-col">
             {/* Command Line Header */}
             <div className="mb-12 font-mono">
                 <div className="flex items-start gap-4 mb-6">
                     <LogoImage size="md" light className="bg-[#1e293b] rounded p-2 border border-[#334155]" />
                     <div>
                         <p className="text-success-400 font-bold text-xl"><span className="text-info-400">const</span> provider = <span className="text-warning-300">'{profile.name}'</span>;</p>
                         <p className="text-neutral-400 text-sm mt-1">// {profile.email}</p>
                         <p className="text-neutral-400 text-sm">// {profile.address}</p>
                     </div>
                 </div>

                 <div className="border-l-2 border-[#334155] pl-6 ml-2 space-y-4">
                     <div>
                         <p className="text-purple-400 text-sm">export default function <span className="text-warning-300">{type.replace(/\s/g, '')}</span>() {'{'}</p>
                     </div>

                     <div className="ml-4">
                         <p className="text-neutral-500 text-xs uppercase tracking-wider mb-1">// Metadata</p>
                         <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
                             <div>
                                 <span className="text-info-400">title:</span> <span className="text-success-300">"{title}"</span>,
                             </div>
                             {metaData.map((m, i) => (
                                 <div key={i}>
                                     <span className="text-info-400">{m.label.toLowerCase().replace(/[^a-z]/g, '')}:</span> <span className="text-success-300">"{m.value}"</span>,
                                 </div>
                             ))}
                         </div>
                     </div>

                     <div className="ml-4 pt-2">
                         <p className="text-neutral-500 text-xs uppercase tracking-wider mb-1">// Client_Config</p>
                         {client ? (
                             <div className="text-sm">
                                 <span className="text-info-400">recipient:</span> {'{'} <br/>
                                 <span className="ml-4 text-neutral-300">name:</span> <span className="text-orange-300">"{client.name}"</span>,<br/>
                                 <span className="ml-4 text-neutral-300">company:</span> <span className="text-orange-300">"{client.company}"</span>,<br/>
                                 <span className="ml-4 text-neutral-300">address:</span> <span className="text-orange-300">"{client.address}"</span><br/>
                                 {'}'}
                             </div>
                         ) : <span className="text-error-400">null</span>}
                     </div>
                 </div>
             </div>
          </div>
        </>
      )}

      <div className="p-10 flex-1 flex flex-col">
         {/* Data Table */}
         <div className="flex-1 tech-table bg-[#1e293b]/50 rounded-lg p-4 border border-[#334155] mb-8">
             <p className="text-neutral-500 text-xs mb-4">$ cat services_list.csv</p>
             {children}
         </div>
         
         {/* Footer Output */}
         <div className="mt-auto border-t border-[#334155] pt-6 font-mono text-xs">
             <p className="text-neutral-400"><span className="text-success-500">➜</span> <span className="text-info-400">~</span> echo "{footerNote}"</p>
             <p className="text-neutral-500 mt-2">{footerNote}</p>
             <div className="mt-4 flex gap-2">
                 <div className="w-2 h-4 bg-neutral-500 animate-pulse"></div>
             </div>
         </div>
      </div>
    </div>
  );
};

export default TechTemplate;
